/**
 * Root-sum-square analysis (PRD §4.5).
 *
 *   sigma_out^2 = sum_i (df/dx_i)^2 sigma_i^2
 *               + 2 sum_{i<j} (df/dx_i)(df/dx_j) rho_ij sigma_i sigma_j
 *
 * The cross terms are not optional garnish. Omitting them makes a matched pair
 * look like two independent parts, which overstates the variance of every
 * ratio-based design — the exact case where the correlation was the design
 * intent.
 *
 * RSS is a first-order method: it linearises at the nominal point and assumes
 * deviations are small. The gap between RSS and Monte Carlo is therefore a
 * direct readout of how nonlinear the system is, and is surfaced as such rather
 * than hidden.
 */

import type { CompiledModel } from './model.ts';
import { compile, type Compiled } from './expr/compile.ts';
import { differentiate, NonDifferentiable } from './expr/differentiate.ts';
import { nominal, stdDev as distStdDev } from './distributions.ts';

export interface RssContribution {
  varId: string;
  partial: number;
  sigma: number;
  /** (df/dx * sigma)^2 — this variable's independent share of output variance. */
  variance: number;
  /** Share of the summed independent variances, in [0,1]. */
  fraction: number;
}

export interface RssResult {
  outputId: string;
  /** Output at the nominal input point. */
  nominal: number;
  variance: number;
  stdDev: number;
  contributions: RssContribution[];
  /** Total contributed by correlation cross terms; negative when correlations cancel. */
  correlationVariance: number;
  numericDerivatives: boolean;
  warnings: string[];
}

export function analyzeRss(
  model: CompiledModel,
  outputIndex: number,
): RssResult {
  const out = model.outputs[outputIndex];
  const n = model.varIds.length;
  const warnings: string[] = [];

  const evalAt = compile(out.inlined, model.varIndex);
  const env = new Float64Array(n);
  for (let i = 0; i < n; i++) env[i] = nominal(model.distributions[i]);
  const nominalValue = evalAt(env);

  // Partials at the nominal point, symbolic where possible.
  const partials = new Float64Array(n);
  let numeric = false;

  for (let i = 0; i < n; i++) {
    let fn: Compiled | null = null;
    try {
      fn = compile(differentiate(out.inlined, model.varIds[i]), model.varIndex);
    } catch (e) {
      if (!(e instanceof NonDifferentiable)) throw e;
      numeric = true;
    }

    if (fn) {
      partials[i] = fn(env);
    } else {
      const scale = Math.max(distStdDev(model.distributions[i]), Math.abs(env[i]) * 1e-6, 1e-12);
      const h = scale * 1e-3;
      const saved = env[i];
      env[i] = saved + h;
      const up = evalAt(env);
      env[i] = saved - h;
      const down = evalAt(env);
      env[i] = saved;
      partials[i] = (up - down) / (2 * h);
    }
  }

  const sigmas = new Float64Array(n);
  for (let i = 0; i < n; i++) sigmas[i] = distStdDev(model.distributions[i]);

  // Independent terms.
  let independentVariance = 0;
  const contributions: RssContribution[] = model.varIds.map((varId, i) => {
    const v = (partials[i] * sigmas[i]) ** 2;
    independentVariance += v;
    return { varId, partial: partials[i], sigma: sigmas[i], variance: v, fraction: 0 };
  });

  // Correlation cross terms. Both mechanisms contribute the same way — the only
  // difference is where rho comes from — and a variable can be in at most one
  // of them, so the two loops cannot double-count a pair.
  let correlationVariance = 0;
  const { varGroup, groups, correlation, varCorrRow } = model;

  for (let i = 0; i < n; i++) {
    if (varGroup[i] < 0) continue;
    for (let j = i + 1; j < n; j++) {
      if (varGroup[j] !== varGroup[i]) continue;
      const rho = groups[varGroup[i]].rho;
      correlationVariance += 2 * partials[i] * partials[j] * rho * sigmas[i] * sigmas[j];
    }
  }

  if (correlation) {
    const k = correlation.ids.length;
    for (let i = 0; i < n; i++) {
      const ri = varCorrRow[i];
      if (ri < 0) continue;
      for (let j = i + 1; j < n; j++) {
        const rj = varCorrRow[j];
        if (rj < 0) continue;
        const rho = correlation.matrix[ri * k + rj];
        if (rho === 0) continue;
        correlationVariance += 2 * partials[i] * partials[j] * rho * sigmas[i] * sigmas[j];
      }
    }
  }

  const totalVariance = Math.max(0, independentVariance + correlationVariance);

  if (independentVariance > 0) {
    for (const c of contributions) c.fraction = c.variance / independentVariance;
  }
  contributions.sort((a, b) => b.variance - a.variance);

  if (numeric) {
    warnings.push(
      'Expression contains abs/min/max/if/rounding; sensitivities are finite-difference ' +
        'approximations and RSS should be cross-checked against Monte Carlo.',
    );
  }
  if (correlationVariance !== 0) {
    warnings.push(
      'Correlation cross terms contribute ' +
        `${correlationVariance >= 0 ? '+' : ''}${correlationVariance.toPrecision(4)} to the output ` +
        'variance. Group correlation is applied in the normal (copula) space, so for ' +
        'strongly non-normal marginals the effective linear correlation is slightly ' +
        'below the configured rho and Monte Carlo is the authority.',
    );
  }
  if (independentVariance + correlationVariance < 0) {
    warnings.push(
      'Cross terms drove the linear variance estimate below zero, which means the ' +
        'linearisation is invalid here. Use the Monte Carlo result.',
    );
  }

  return {
    outputId: out.spec.id,
    nominal: nominalValue,
    variance: totalVariance,
    stdDev: Math.sqrt(totalVariance),
    contributions,
    correlationVariance,
    numericDerivatives: numeric,
    warnings,
  };
}
